<template>
  <transition name="ik-sheet-slide">
    <div
      v-if="
        store.showBanner &&
        disclaimerStore.accepted &&
        route.path !== '/cookie-policy'
      "
      class="ik-cookie-sheet"
    >
      <button class="ik-cookie-close" @click="handleClose" aria-label="Close">
        <i class="ri-close-line"></i>
      </button>

      <div class="ik-cookie-content">
        <p>
          IndieKonnect uses cookies and similar tracking tools to operate this
          website, understand how visitors use it, remember your preferences,
          and where you allow it show you more relevant content. This helps us
          keep the site secure and improve your overall experience.
        </p>
        <p>
          By clicking "Accept All", you consent to our use of all cookie
          categories described in our
          <router-link to="/cookie-policy">Cookie Policy</router-link>. If you'd
          prefer more control, click "Manage Preferences" to switch individual
          categories on or off. If you close this banner or keep browsing
          without making a selection, only Strictly Necessary cookies will be
          used; this may limit some website features.
        </p>
        <p>
          You can change or withdraw your cookie consent at any time through the
          "Cookie Preferences" link in the website footer. This will not affect
          the lawfulness of any use of cookies before you withdraw consent.
        </p>
        <p class="ik-cookie-note">
          For more details, see our
          <router-link to="/privacy-policy">Privacy Policy</router-link> and
          <router-link to="/cookie-policy">Cookie Policy</router-link>.
        </p>
      </div>

      <div class="ik-cookie-actions">
        <button class="ik-btn ik-btn-outline" @click="goToPreferences">
          Cookie Preferences
        </button>
        <button class="ik-btn ik-btn-decline" @click="handleNecessary">
          Necessary Cookies
        </button>
        <button class="ik-btn ik-btn-accept" @click="handleAcceptAll">
          Accept All
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useCookieConsentStore } from "@/stores/useCookieConsentStore";
import { useDisclaimerStore } from "@/stores/useDisclaimerStore";

const store = useCookieConsentStore();
const disclaimerStore = useDisclaimerStore();
const router = useRouter();
const route = useRoute();

let pollId = null;

onMounted(() => {
  store.init();

  window.addEventListener("focus", store.resync);

  // Catches manual cookie deletion (e.g. via DevTools) during an active
  // session, when neither a route change nor a window focus event fires.
  pollId = window.setInterval(() => {
    store.resync();
  }, 2000);
});

onBeforeUnmount(() => {
  window.removeEventListener("focus", store.resync);
  if (pollId) {
    window.clearInterval(pollId);
    pollId = null;
  }
});

watch(
  () => route.path,
  () => {
    store.resync();
  },
);

function handleAcceptAll() {
  store.acceptAll();
}

function handleNecessary() {
  store.acceptNecessaryOnly();
}

function handleClose() {
  store.acceptNecessaryOnly();
}

function goToPreferences() {
  router.push("/cookie-policy");
}
</script>

<style scoped>
.ik-cookie-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9000; /* below DisclaimerModal's 9999, so it stays hidden until that's resolved */
  background: #ffffff;
  border-top: 3px solid #ffc72c;
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.25);
  padding: 20px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.ik-cookie-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #888;
  cursor: pointer;
  line-height: 1;
}

.ik-cookie-close:hover {
  color: #003da5;
}

.ik-cookie-content {
  max-width: 900px;
  margin: 0 auto;
  padding-right: 24px;
}

.ik-cookie-content p {
  font-size: 13.5px;
  line-height: 1.55;
  color: #2b2b2b;
  margin: 0 0 10px;
}

.ik-cookie-content a,
.ik-cookie-content :deep(a) {
  color: #003da5;
  font-weight: 600;
  text-decoration: underline;
}

.ik-cookie-note {
  color: #555;
}

.ik-cookie-actions {
  max-width: 900px;
  margin: 12px auto 0;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ik-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s ease;
}

.ik-btn:hover {
  opacity: 0.9;
}

.ik-btn-accept {
  background: #ffc72c;
  color: #003da5;
  margin-left: auto;
}

.ik-btn-decline {
  background: #f2f2f2;
  color: #555;
  border: 1px solid #ddd;
}

.ik-btn-outline {
  background: #ffffff;
  color: #003da5;
  border: 1px solid #003da5;
}

@media (max-width: 640px) {
  .ik-cookie-actions {
    flex-direction: column;
  }
  .ik-btn-accept {
    margin-left: 0;
  }
}

/* slide-up transition */
.ik-sheet-slide-enter-active,
.ik-sheet-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.ik-sheet-slide-enter-from,
.ik-sheet-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
